import axios from 'axios';
import {
  VisualizationSession,
  PaginatedData,
  FileValidationResult,
  ScheMatiQConfig,
  ScheMatiQStatus,
  SchemaExtractionResult,
  DocumentUploadResult,
  CloudDocumentAddResult,
  DocumentProcessingResult,
  ProcessingStatus,
  SchemaData,
  EditColumnRequest,
  AddColumnRequest,
  MergeColumnsRequest,
  ReprocessRequest,
  SchemaEditResponse,
  ReprocessingStatus,
  SchemaValidationResult as SchemaValidationResultType,
  SchemaChangeStatus,
  PaperDiscoveryResult,
  ReextractionRequest,
  ReextractionResponse,
  ReextractionOperationStatus,
  ContinueDiscoveryDocuments,
  ContinueDiscoveryRequest,
  ContinueDiscoveryResponse,
  ContinueDiscoveryStatus,
  ConfirmColumnsRequest,
  ConfirmColumnsResponse,
  DocumentAvailabilityRequest,
  DocumentAvailabilityResponse,
  CostEstimate,
  ChatToolInfo,
  ChatMessageResponse,
  ChatMessagesResponse,
} from '../types';
import {
  UnitListResponse,
  UnitSuggestionsResponse,
  MergeUnitsRequest,
  MergeUnitsResponse,
  DocumentListResponse,
} from '../types/unit';
import { FilterRule, SortColumn } from '../components/DataTable/types/filters';

// Railway backend URL - used when env vars aren't set at build time
const RAILWAY_BACKEND_URL = 'https://backend-production-5a26.up.railway.app';

// Determine API base URL
function getApiBaseUrl(): string {
  // Build-time env var (preferred)
  if (process.env.REACT_APP_API_URL) {
    return `${process.env.REACT_APP_API_URL}/api`;
  }

  // Runtime detection for Railway or custom domain
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('railway.app') || hostname.includes('up.railway.app') || hostname.includes('schematiq-ai.com')) {
      return `${RAILWAY_BACKEND_URL}/api`;
    }
  }

  // Local development - use relative path (proxy handles it)
  return '/api';
}

const API_BASE = getApiBaseUrl();

// Export for WebSocket service to use
export const getBackendBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('railway.app') || hostname.includes('up.railway.app') || hostname.includes('schematiq-ai.com')) {
      return RAILWAY_BACKEND_URL;
    }
  }
  return 'http://localhost:8000';
};

// One cell in a PUT /schematiq/cells batch.
export type CellBatchItem = {
  row_name: string;
  column: string;
  value: string;
  row_index?: number;
  source_document?: string;
  is_restore?: boolean;
  restore?: unknown;
};

// Max cells per batch request. Must stay at or below the backend's
// MAX_CELL_BATCH (routes/schematiq.py).
export const CELL_BATCH_SIZE = 500;
// A batch is one file read/rewrite/upload, but give large data files headroom.
const CELL_BATCH_TIMEOUT_MS = 120000;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// Feedback API (release mode only)
export const feedbackAPI = {
  submitFeedback: async (data: {
    session_id: string;
    rating: 'positive' | 'negative';
    comment?: string;
    table_row_count: number;
    table_column_count: number;
  }): Promise<{ status: string }> => {
    const response = await api.post('/feedback/table', data);
    return response.data;
  },
};

// Support API (issue reports) — always available
export const supportAPI = {
  // Fetch the complete project export JSON as text (same artifact as "Save project").
  // Best-effort: returns undefined on any failure so a report can still be sent.
  fetchProjectJson: async (
    sessionId: string,
    sessionMode: 'schematiq' | 'load' | string,
  ): Promise<string | undefined> => {
    try {
      const tzOffset = new Date().getTimezoneOffset();
      const path = sessionMode === 'schematiq'
        ? `/schematiq/export-complete/${sessionId}?format=json&tz_offset=${tzOffset}`
        : `/load/export-complete/${sessionId}?format=json`;
      const response = await api.get(path, { responseType: 'text', transformResponse: (d) => d });
      return typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
    } catch {
      return undefined;
    }
  },
  reportIssue: async (data: {
    session_id?: string;
    description: string;
    reporter_email?: string;
    project_json?: string;
    screenshots?: { name?: string; mime: string; data_b64: string }[];
    client_context?: Record<string, unknown>;
  }): Promise<{ status: string }> => {
    const response = await api.post('/support/report', data);
    return response.data;
  },
};

// Config API (for public configuration)
export type PublicConfig = {
  max_documents: number;
  developer_mode: boolean;
  allow_llm_config: boolean;
  data_collection_enabled: boolean;
  release_config: Record<string, any>;
  server_has_api_keys: boolean;
};

// Fourteen call sites across the app fetch /config independently, and several
// of them mount together: opening a project issued two identical requests in
// the same tick (Workspace's developer-mode check and LLMSelector). This
// collapses concurrent callers onto one request.
//
// Deliberately in-flight only, with no TTL. /api/config is not static -- it
// reports `active_sessions` from the concurrency limiter -- so caching a
// resolved response would hand later callers a stale count. Nothing in the
// frontend reads that field today, but a time-based cache would silently break
// whatever does first. Callers that mount later still get fresh data.
let inFlightConfig: Promise<PublicConfig> | null = null;

export const configAPI = {
  getConfig: async (): Promise<PublicConfig> => {
    if (inFlightConfig) return inFlightConfig;
    inFlightConfig = api
      .get('/config')
      .then((response) => response.data as PublicConfig)
      .finally(() => {
        inFlightConfig = null;
      });
    return inFlightConfig;
  },

  /** Drop any in-flight request so the next getConfig() starts a fresh one. */
  invalidate: () => {
    inFlightConfig = null;
  },
};

// Load API (for loading existing ScheMatiQ data)
export const loadAPI = {
  uploadFile: async (file: File): Promise<{
    session_id: string;
    validation: FileValidationResult;
    requires_column_mapping: boolean;
  }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/load/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  parseFile: async (sessionId: string, columnMappings?: Record<string, string>): Promise<void> => {
    const payload = columnMappings ? {
      session_id: sessionId,
      column_mappings: columnMappings,
      column_types: {}
    } : null;

    await api.post(`/load/parse/${sessionId}`, payload);
  },

  getData: async (
    sessionId: string,
    page = 0,
    pageSize = 50,
    filters?: FilterRule[],
    sort?: SortColumn[],
    search?: string,
    documents?: string[]
  ): Promise<PaginatedData> => {
    // Use POST for filter/sort support
    const params: Record<string, any> = { page, page_size: pageSize };
    if (documents && documents.length > 0) {
      params.documents = documents.join(',');
    }
    const response = await api.post(`/load/data/${sessionId}`, {
      filters: filters && filters.length > 0 ? filters : null,
      sort: sort && sort.length > 0 ? sort : null,
      search: search || null
    }, { params });
    return response.data;
  },

  getSession: async (sessionId: string): Promise<VisualizationSession> => {
    const response = await api.get(`/load/sessions/${sessionId}`);
    return response.data;
  },

  deleteSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/load/sessions/${sessionId}`);
  },

  // Enhanced load workflow methods
  extractSchema: async (sessionId: string, query?: string): Promise<SchemaExtractionResult> => {
    const response = await api.post(`/load/extract-schema/${sessionId}`, null, {
      params: { query: query || '' }
    });
    return response.data;
  },

  addDocuments: async (sessionId: string, files: File[], bypassLimit = false): Promise<DocumentUploadResult> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    // Files are processed sequentially server-side (incl. PDF figure extraction
    // via Docling's CPU-based layout/table models), so the default 30s timeout
    // isn't enough even for a single dense/long paper — one real 9-page paper
    // measured at ~28s for conversion alone, before request overhead. Scale it
    // like addCloudDocuments below, but with a much higher floor/per-file rate
    // since this path does real per-file CPU-bound ML inference, not just a
    // cloud copy. Docling's models are loaded when the backend starts
    // (figure_extraction_service.warm_up_converter), so this doesn't need to
    // cover a model-load cold start.
    const timeoutMs = Math.max(120000, files.length * 60000);
    const response = await api.post(
      `/load/add-documents/${sessionId}?bypass_limit=${bypassLimit}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: timeoutMs,
      }
    );

    return response.data;
  },

  processDocuments: async (sessionId: string, llmConfig?: any): Promise<DocumentProcessingResult> => {
    const requestBody = llmConfig ? { llm_config: llmConfig } : {};
    const response = await api.post(`/load/process-documents/${sessionId}`, requestBody);
    return response.data;
  },

  /**
   * Start schema rediscovery for an imported (UPLOAD-type) session — the
   * equivalent of schematiqAPI.resume for a session that never went through
   * /schematiq/configure. Only succeeds if the session's rows already
   * reference resolvable source documents.
   */
  rediscoverImported: async (sessionId: string): Promise<{ message: string; session_id: string }> => {
    const response = await api.post(`/load/rediscover/${sessionId}`);
    return response.data;
  },

  confirmWebSocketReady: async (sessionId: string): Promise<{ status: string; connections: number; session_id: string }> => {
    const response = await api.post(`/load/sessions/${sessionId}/confirm-websocket`);
    return response.data;
  },

  removeDocument: async (sessionId: string, filename: string): Promise<{
    status: string;
    message: string;
    remaining_documents: string[];
    files_removed: string[];
  }> => {
    const response = await api.delete(`/load/remove-document/${sessionId}`, {
      data: { filename }
    });
    return response.data;
  },

  getProcessingStatus: async (sessionId: string): Promise<ProcessingStatus> => {
    const response = await api.get(`/load/processing-status/${sessionId}`);
    return response.data;
  },

  stopProcessing: async (sessionId: string): Promise<{
    status: string;
    message: string;
    stopped: boolean;
    processed_documents?: number;
    total_documents?: number;
    data_rows_saved?: number;
  }> => {
    const response = await api.post(`/load/stop-processing/${sessionId}`);
    return response.data;
  },

  exportData: async (sessionId: string): Promise<Blob> => {
    const response = await api.get(`/load/export/${sessionId}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

// ScheMatiQ API
export const schematiqAPI = {
  configure: async (config: ScheMatiQConfig): Promise<{ session_id: string; message: string }> => {
    const response = await api.post('/schematiq/configure', config);
    return response.data;
  },

  run: async (sessionId: string): Promise<void> => {
    await api.post(`/schematiq/run/${sessionId}`);
  },

  resume: async (sessionId: string): Promise<void> => {
    await api.post(`/schematiq/resume/${sessionId}`);
  },

  getStatus: async (sessionId: string): Promise<ScheMatiQStatus> => {
    const response = await api.get(`/schematiq/status/${sessionId}`);
    return response.data;
  },

  getConfig: async (sessionId: string): Promise<ScheMatiQConfig> => {
    const response = await api.get(`/schematiq/config/${sessionId}`);
    return response.data;
  },

  getSchema: async (sessionId: string): Promise<SchemaData> => {
    const response = await api.get(`/schematiq/schema/${sessionId}`);
    return response.data;
  },

  getData: async (
    sessionId: string,
    page = 0,
    pageSize = 50,
    filters?: FilterRule[],
    sort?: SortColumn[],
    search?: string,
    documents?: string[]
  ): Promise<PaginatedData> => {
    // Use POST for filter/sort support
    const params: Record<string, any> = { page, page_size: pageSize };
    if (documents && documents.length > 0) {
      params.documents = documents.join(',');
    }
    const response = await api.post(`/schematiq/data/${sessionId}`, {
      filters: filters && filters.length > 0 ? filters : null,
      sort: sort && sort.length > 0 ? sort : null,
      search: search || null
    }, { params });
    return response.data;
  },

  stop: async (sessionId: string): Promise<{
    status: string;
    message: string;
  }> => {
    const response = await api.post(`/schematiq/stop/${sessionId}`);
    return response.data;
  },

  getDirectories: async (): Promise<{ value: string; label: string }[]> => {
    const response = await api.get('/schematiq/directories');
    return response.data;
  },

  getSchemaFiles: async (): Promise<{
    value: string;
    label: string;
    columns_count: number;
    preview: string;
    columns: {
      name: string;
      definition: string;
      rationale: string;
      allowed_values?: string[];
    }[];
  }[]> => {
    const response = await api.get('/schematiq/schema-files');
    return response.data;
  },

  export: async (sessionId: string): Promise<void> => {
    const response = await api.get(`/schematiq/export/${sessionId}`, {
      responseType: 'blob'
    });

    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `schematiq_data_${sessionId.slice(0, 8)}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  },

  updateCell: async (
    sessionId: string,
    rowName: string,
    column: string,
    value: string,
    sourceDocument?: string,
    rowIndex?: number
  ): Promise<{ status: string; session_id: string; row_name: string; column: string; value: string; previous_value?: any }> => {
    const params: Record<string, string | number> = { row_name: rowName, column, value };
    if (sourceDocument) params.source_document = sourceDocument;
    // Always send the stable row index when we have it. The backend matches by
    // row_name first and only uses row_index as a fallback, so this is safe even
    // when a name is present; it lets clears/edits still resolve when the row's
    // stored identity key differs from the displayed name (e.g. By-Document rows
    // keyed under _unit_name), which otherwise failed with "row not found".
    if (rowIndex != null) params.row_index = rowIndex;
    const response = await api.put(`/schematiq/cell/${sessionId}`, null, { params });
    return response.data;
  },

  /**
   * Write many cells in one request (column clear, multi-cell paste, undo of
   * either). The backend rewrites and persists the data file once for the
   * whole batch, so a large clear no longer runs past the client timeout the
   * way one request per cell did. Each entry carries the same identifiers as
   * updateCell; `is_restore` + `restore` mirror restoreCell's body. The
   * response has one `{ ok, error }` result per entry, in request order.
   */
  updateCells: async (
    sessionId: string,
    updates: CellBatchItem[],
  ): Promise<{ status: string; succeeded: number; failed: number; results: Array<{ ok: boolean; error?: string | null }> }> => {
    const response = await api.put(
      `/schematiq/cells/${sessionId}`,
      { updates },
      { timeout: CELL_BATCH_TIMEOUT_MS },
    );
    return response.data;
  },

  /**
   * Restore a cell to its previous full value (undo).
   *
   * Takes the same identifiers as updateCell, rowIndex included. The two must
   * stay in step: the backend resolves the target row from whatever it is given,
   * so an undo that omits an identifier the original edit supplied can resolve
   * to a different row. That is worse than a failed write, because it silently
   * reverts a cell the user never touched.
   */
  restoreCell: async (
    sessionId: string,
    rowName: string,
    column: string,
    previousValue: any,
    sourceDocument?: string,
    rowIndex?: number
  ): Promise<{ status: string }> => {
    const params: Record<string, string | number> = { row_name: rowName, column, value: '' };
    if (sourceDocument) params.source_document = sourceDocument;
    if (rowIndex != null) params.row_index = rowIndex;
    const response = await api.put(`/schematiq/cell/${sessionId}`, { restore: previousValue }, { params });
    return response.data;
  },

  /**
   * Estimate cost for ScheMatiQ execution for an existing configured session.
   */
  estimateCost: async (sessionId: string): Promise<CostEstimate> => {
    const response = await api.post(`/schematiq/estimate-cost/${sessionId}`);
    return response.data;
  },

  /**
   * Preview cost estimate without saving a session.
   * Useful for getting estimates before committing to a configuration.
   * @param config - The ScheMatiQ configuration
   * @param uploadedFiles - Optional array of uploaded file info (name, size) for estimation
   */
  estimateCostPreview: async (
    config: ScheMatiQConfig,
    uploadedFiles?: Array<{ name: string; size: number }>
  ): Promise<CostEstimate> => {
    const response = await api.post('/schematiq/estimate-cost-preview', { 
      config, 
      uploaded_files: uploadedFiles 
    });
    return response.data;
  },
};

// Common session API
export const sessionAPI = {
  getSession: async (sessionId: string, type: 'load' | 'schematiq'): Promise<VisualizationSession> => {
    if (type === 'load') {
      return loadAPI.getSession(sessionId);
    } else {
      // For ScheMatiQ, we need to construct from multiple endpoints
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _status = await schematiqAPI.getStatus(sessionId);
      // This would need to be implemented based on actual backend structure
      throw new Error('ScheMatiQ session details not yet implemented');
    }
  },

  getData: async (
    sessionId: string,
    type: 'load' | 'schematiq',
    page = 0,
    pageSize = 50,
    filters?: FilterRule[],
    sort?: SortColumn[],
    search?: string,
    documents?: string[]
  ): Promise<PaginatedData> => {
    if (type === 'load') {
      return loadAPI.getData(sessionId, page, pageSize, filters, sort, search, documents);
    } else {
      return schematiqAPI.getData(sessionId, page, pageSize, filters, sort, search, documents);
    }
  },
};

// Schema editing API
// Cloud Data API (datasets and templates)
export const cloudAPI = {
  // List available datasets
  getDatasets: async (): Promise<{
    name: string;
    path: string;
    file_count: number;
    description?: string;
  }[]> => {
    const response = await api.get('/cloud/datasets');
    return response.data;
  },

  // List files in a dataset
  getDatasetFiles: async (datasetName: string): Promise<{
    name: string;
    path: string;
    size: number;
    content_type?: string;
  }[]> => {
    const response = await api.get(`/cloud/datasets/${encodeURIComponent(datasetName)}/files`);
    return response.data;
  },

  // List available templates
  getTemplates: async (): Promise<{
    name: string;
    path: string;
    file_type: string;
    description?: string;
    row_count?: number;
    column_count?: number;
  }[]> => {
    const response = await api.get('/cloud/templates');
    return response.data;
  },

  // Get template info
  getTemplate: async (templateName: string): Promise<{
    name: string;
    path: string;
    file_type: string;
    description?: string;
    row_count?: number;
    column_count?: number;
  }> => {
    const response = await api.get(`/cloud/templates/${encodeURIComponent(templateName)}`);
    return response.data;
  },

  // List all cloud documents grouped by dataset
  getCloudDocuments: async (): Promise<{
    dataset: string;
    files: {
      name: string;
      path: string;
      size: number;
      content_type?: string;
    }[];
  }[]> => {
    const response = await api.get('/cloud/documents');
    return response.data;
  },

  // Load a template and create a session
  loadTemplate: async (templateName: string): Promise<{
    session_id: string;
    template_name: string;
    status: string;
    message: string;
    row_count: number;
    column_count: number;
  }> => {
    const response = await api.post(`/load/template/${encodeURIComponent(templateName)}`);
    return response.data;
  },

  // Add cloud documents to a session
  addCloudDocuments: async (sessionId: string, dataset: string, files: string[]): Promise<CloudDocumentAddResult> => {
    const timeoutMs = Math.max(30000, files.length * 1000);
    const response = await api.post(`/load/add-cloud-documents/${sessionId}`, {
      dataset,
      files
    }, { timeout: timeoutMs });
    return response.data;
  },

  // List available initial schemas from cloud storage
  getInitialSchemas: async (): Promise<{
    name: string;
    path: string;
    file_type: string;
    columns_count: number;
    preview: string;
    columns: {
      name: string;
      definition: string;
      rationale: string;
      allowed_values?: string[];
    }[];
  }[]> => {
    const response = await api.get('/cloud/initial-schemas');
    return response.data;
  },

  // Get specific initial schema
  getInitialSchema: async (schemaName: string): Promise<{
    name: string;
    path: string;
    file_type: string;
    columns_count: number;
    preview: string;
    columns: {
      name: string;
      definition: string;
      rationale: string;
      allowed_values?: string[];
    }[];
  }> => {
    const response = await api.get(`/cloud/initial-schemas/${encodeURIComponent(schemaName)}`);
    return response.data;
  },

  // Upload a new initial schema file
  uploadInitialSchema: async (file: File): Promise<{
    status: string;
    name: string;
    path: string;
    columns_count: number;
  }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/cloud/initial-schemas/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Schema editing API
export const schemaAPI = {
  editColumn: async (sessionId: string, request: EditColumnRequest): Promise<SchemaEditResponse> => {
    const response = await api.put(`/schema/edit-column/${sessionId}`, request);
    return response.data;
  },

  deleteColumn: async (sessionId: string, columnName: string): Promise<SchemaEditResponse> => {
    const response = await api.delete(`/schema/delete-column/${sessionId}/${encodeURIComponent(columnName)}`);
    return response.data;
  },

  addColumn: async (sessionId: string, request: AddColumnRequest): Promise<SchemaEditResponse> => {
    const response = await api.post(`/schema/add-column/${sessionId}`, request);
    return response.data;
  },

  mergeColumns: async (sessionId: string, request: MergeColumnsRequest): Promise<SchemaEditResponse> => {
    const response = await api.post(`/schema/merge-columns/${sessionId}`, request);
    return response.data;
  },

  reprocessDocuments: async (sessionId: string, request: ReprocessRequest): Promise<{ success: boolean; message: string; session_id: string; }> => {
    const response = await api.post(`/schema/reprocess/${sessionId}`, request);
    return response.data;
  },

  getReprocessingStatus: async (sessionId: string): Promise<ReprocessingStatus> => {
    const response = await api.get(`/schema/reprocessing-status/${sessionId}`);
    return response.data;
  },

  validateSchema: async (sessionId: string): Promise<SchemaValidationResultType> => {
    const response = await api.get(`/schema/validation/${sessionId}`);
    return response.data;
  },

  backupSchema: async (sessionId: string): Promise<{ success: boolean; backup_id: string; backup_path: string; created_at: string; includes_data: boolean; }> => {
    const response = await api.post(`/schema/backup/${sessionId}`);
    return response.data;
  },

  restoreSchema: async (sessionId: string, backupId: string): Promise<SchemaEditResponse> => {
    const response = await api.post(`/schema/restore/${sessionId}`, { backup_id: backupId });
    return response.data;
  },

  // Schema Evolution / Suggestions API
  getSuggestions: async (sessionId: string): Promise<{
    session_id: string;
    suggestions: Array<{
      column_name: string;
      pending_values?: Array<{
        value: string;
        document_count: number;
        first_seen: string;
        documents: string[];
      }>;
      current_allowed_values?: string[];
      auto_expand_threshold?: number;
      suggested_values?: string[];
      value_details?: Record<string, {
        value: string;
        document_count: number;
        first_seen: string;
        documents: string[];
      }>;
      auto_approved?: boolean;
    }>;
    total_pending: number;
  }> => {
    const response = await api.get(`/schema/suggestions/${sessionId}`);
    return response.data;
  },

  approveSuggestion: async (sessionId: string, columnName: string, value: string): Promise<{ status: string; message: string }> => {
    const response = await api.post(`/schema/approve-suggestion/${sessionId}`, {
      column_name: columnName,
      value: value
    });
    return response.data;
  },

  rejectSuggestion: async (sessionId: string, columnName: string, value: string): Promise<{ status: string; message: string }> => {
    const response = await api.post(`/schema/reject-suggestion/${sessionId}`, {
      column_name: columnName,
      value: value
    });
    return response.data;
  },

  setAutoExpandThreshold: async (sessionId: string, columnName: string, threshold: number): Promise<{ status: string; message: string; threshold: number }> => {
    const response = await api.put(`/schema/auto-expand-threshold/${sessionId}/${encodeURIComponent(columnName)}`, {
      threshold: threshold
    });
    return response.data;
  },

  bulkApproveSuggestions: async (sessionId: string, columnName?: string): Promise<{ status: string; message: string; approved_count: number }> => {
    const params = columnName ? `?column_name=${encodeURIComponent(columnName)}` : '';
    const response = await api.post(`/schema/bulk-approve/${sessionId}${params}`);
    return response.data;
  },

  // Document availability pre-check
  precheckDocuments: async (sessionId: string, request: DocumentAvailabilityRequest): Promise<DocumentAvailabilityResponse> => {
    const response = await api.post(`/schema/precheck-documents/${sessionId}`, request);
    return response.data;
  },

  // Re-extraction API methods
  getSchemaChangeStatus: async (sessionId: string): Promise<SchemaChangeStatus> => {
    const response = await api.get(`/schema/change-status/${sessionId}`);
    return response.data;
  },

  discoverPapers: async (sessionId: string): Promise<PaperDiscoveryResult> => {
    const response = await api.get(`/schema/discover-papers/${sessionId}`);
    return response.data;
  },

  startReextraction: async (sessionId: string, request: ReextractionRequest): Promise<ReextractionResponse> => {
    const response = await api.post(`/schema/reextract/${sessionId}`, request);
    return response.data;
  },

  getReextractionStatus: async (sessionId: string, operationId: string): Promise<ReextractionOperationStatus> => {
    const response = await api.get(`/schema/reextraction-status/${sessionId}/${operationId}`);
    return response.data;
  },

  stopReextraction: async (sessionId: string, operationId: string): Promise<{
    status: string;
    message: string;
    processed_documents: number;
    total_documents: number;
  }> => {
    const response = await api.post(`/schema/stop-reextraction/${sessionId}/${operationId}`);
    return response.data;
  },

  captureBaseline: async (sessionId: string): Promise<{ status: string; message: string; column_count: number }> => {
    const response = await api.post(`/schema/capture-baseline/${sessionId}`);
    return response.data;
  },

  uploadMissingPapers: async (sessionId: string, files: File[]): Promise<{ status: string; message: string; uploaded_files: string[] }> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await api.post(`/schema/upload-missing-papers/${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Continue Schema Discovery API methods
  continueDiscovery: {
    getDocuments: async (sessionId: string): Promise<ContinueDiscoveryDocuments> => {
      const response = await api.get(`/schema/continue-discovery/documents/${sessionId}`);
      return response.data;
    },

    start: async (sessionId: string, request: ContinueDiscoveryRequest): Promise<ContinueDiscoveryResponse> => {
      const response = await api.post(`/schema/continue-discovery/start/${sessionId}`, request);
      return response.data;
    },

    getStatus: async (sessionId: string, operationId: string): Promise<ContinueDiscoveryStatus> => {
      const response = await api.get(`/schema/continue-discovery/status/${sessionId}/${operationId}`);
      return response.data;
    },

    confirmColumns: async (sessionId: string, operationId: string, request: ConfirmColumnsRequest): Promise<ConfirmColumnsResponse> => {
      const response = await api.post(`/schema/continue-discovery/confirm/${sessionId}/${operationId}`, request);
      return response.data;
    },

    stop: async (sessionId: string, operationId: string): Promise<{ status: string; phase: string; message: string }> => {
      const response = await api.post(`/schema/continue-discovery/stop/${sessionId}/${operationId}`);
      return response.data;
    },
  },
};

// Observation Unit API
export const observationUnitAPI = {
  list: async (sessionId: string): Promise<{
    session_id: string;
    observation_units: Array<{
      unit_name: string;
      document_id?: string;
      confidence?: number;
    }>;
    count: number;
  }> => {
    const response = await api.get(`/observation-unit/list/${sessionId}`);
    return response.data;
  },

  remove: async (sessionId: string, unitName: string): Promise<{
    status: string;
    message: string;
    session_id: string;
    observation_units: Array<{
      unit_name: string;
      document_id?: string;
      confidence?: number;
    }>;
    row_count: number;
  }> => {
    const response = await api.delete(`/observation-unit/remove/${sessionId}`, {
      data: { unit_name: unitName }
    });
    return response.data;
  },

  add: async (sessionId: string, request: {
    unit_name: string;
    document_id?: string;
    relevant_passages?: string[];
    confidence?: number;
  }): Promise<{
    status: string;
    message: string;
    session_id: string;
    observation_units: Array<{
      unit_name: string;
      document_id?: string;
      confidence?: number;
    }>;
    row_count: number;
  }> => {
    const response = await api.post(`/observation-unit/add/${sessionId}`, request);
    return response.data;
  },

  updateDefinition: async (sessionId: string, request: {
    name: string;
    definition: string;
    example_names?: string[];
  }): Promise<{
    status: string;
    message: string;
    observation_unit: {
      name: string;
      definition: string;
      example_names?: string[];
      source_document?: string;
      discovery_iteration?: number;
    };
    warning?: string;
  }> => {
    const response = await api.patch(`/observation-unit/definition/${sessionId}`, request);
    return response.data;
  },

  removeBulk: async (sessionId: string, unitNames: string[]): Promise<{
    status: string;
    message: string;
    session_id: string;
    deleted_count: number;
    failed: string[];
  }> => {
    const response = await api.delete(`/observation-unit/remove-bulk/${sessionId}`, {
      data: { unit_names: unitNames }
    });
    return response.data;
  },
};

/** A source file the attach endpoint declined to store, with a human-readable reason. */
export interface SkippedAttachment {
  name: string;
  reason: string;
}

/**
 * Build a concise, human-readable summary of files the attach endpoint skipped
 * (e.g. over the size limit), for a toast or inline notice. Returns '' when
 * nothing was skipped. Caps the list so a large batch doesn't overflow the UI.
 */
export const describeSkippedAttachments = (skipped: SkippedAttachment[]): string => {
  if (!skipped || skipped.length === 0) return '';
  const MAX_SHOWN = 5;
  const shown = skipped
    .slice(0, MAX_SHOWN)
    .map((s) => `${s.name} (${s.reason})`)
    .join(', ');
  const extra = skipped.length > MAX_SHOWN ? `, and ${skipped.length - MAX_SHOWN} more` : '';
  return shown + extra;
};

// Units API (Observation Unit View and Merge)
export const unitsAPI = {
  /**
   * List all observation units in a session.
   */
  list: async (sessionId: string): Promise<UnitListResponse> => {
    const response = await api.get(`/units/list/${sessionId}`);
    // Convert snake_case to camelCase for frontend
    const data = response.data;
    return {
      units: data.units.map((u: any) => ({
        name: u.name,
        rowCount: u.row_count,
        sourceDocuments: u.source_documents,
        isMerged: u.is_merged,
        originalUnits: u.original_units,
      })),
      totalUnits: data.total_units,
      totalRows: data.total_rows,
    };
  },

  /**
   * Get paginated data grouped by observation unit, with optional
   * search/filters/sort applied at the row level across the full session.
   */
  getData: async (
    sessionId: string,
    options?: {
      units?: string[];
      page?: number;
      pageSize?: number;
      filters?: FilterRule[];
      sort?: SortColumn[];
      search?: string;
    }
  ): Promise<PaginatedData> => {
    const params: Record<string, any> = {};
    if (options?.units && options.units.length > 0) params.units = options.units.join(',');
    if (options?.page !== undefined) params.page = options.page;
    if (options?.pageSize !== undefined) params.page_size = options.pageSize;

    const response = await api.post(
      `/units/data/${sessionId}`,
      {
        filters: options?.filters && options.filters.length > 0 ? options.filters : null,
        sort: options?.sort && options.sort.length > 0 ? options.sort : null,
        search: options?.search || null,
      },
      { params }
    );
    return response.data;
  },

  /**
   * Build the URL that serves a source document's raw bytes for inline viewing.
   * Works both as an <iframe> src and as an anchor href (open in a new tab).
   */
  getDocumentContentUrl: (sessionId: string, name: string): string =>
    `${API_BASE}/units/document-content/${encodeURIComponent(sessionId)}?name=${encodeURIComponent(name)}`,

  /**
   * Build the URL that serves an extracted figure's image bytes for inline
   * viewing — a cell whose citation is figure-typed (see FigureExcerpt)
   * points here instead of at getDocumentContentUrl's whole source document.
   */
  getFigureContentUrl: (sessionId: string, figureId: string): string =>
    `${API_BASE}/units/figure-content/${encodeURIComponent(sessionId)}?figure_id=${encodeURIComponent(figureId)}`,

  /**
   * Fetch a source document's raw text via the shared axios instance (so it
   * reuses the configured base URL, credentials, and interceptors). Used by the
   * highlight-capable preview to render text documents in a DOM we control.
   */
  getDocumentContentText: async (sessionId: string, name: string): Promise<string> => {
    const response = await api.get<string>(
      `/units/document-content/${encodeURIComponent(sessionId)}?name=${encodeURIComponent(name)}`,
      { responseType: 'text', transformResponse: [(d) => d] },
    );
    return typeof response.data === 'string' ? response.data : String(response.data ?? '');
  },

  /**
   * Re-attach original source files to a session so the document viewer can
   * render them. View-only: does not change session status or queue the files
   * for processing (unlike loadAPI.addDocuments). Used to restore previews for
   * imported projects, which carry schema + data but not the original files.
   */
  attachSourceDocuments: async (
    sessionId: string,
    files: File[],
  ): Promise<{ status: string; attached: string[]; skipped: SkippedAttachment[] }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const response = await api.post(
      `/units/source-documents/${encodeURIComponent(sessionId)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  /**
   * Get list of source documents with row counts.
   */
  getDocuments: async (sessionId: string): Promise<DocumentListResponse> => {
    const response = await api.get(`/units/documents/${sessionId}`);
    const data = response.data;
    return {
      documents: data.documents.map((d: any) => ({
        name: d.name,
        rowCount: d.row_count,
        url: d.url || undefined,
      })),
      totalDocuments: data.total_documents,
      totalRows: data.total_rows,
      enrichmentPending: data.enrichment_pending ?? false,
    };
  },

  /**
   * Merge multiple observation units into one.
   */
  merge: async (sessionId: string, request: MergeUnitsRequest): Promise<MergeUnitsResponse> => {
    const response = await api.post(`/units/merge/${sessionId}`, request);
    const data = response.data;
    return {
      success: data.success,
      message: data.message,
      merged_unit: data.merged_unit ? {
        name: data.merged_unit.name,
        rowCount: data.merged_unit.row_count,
        sourceDocuments: data.merged_unit.source_documents,
        isMerged: data.merged_unit.is_merged,
        originalUnits: data.merged_unit.original_units,
      } : undefined,
      rows_affected: data.rows_affected,
    };
  },

  /**
   * Get suggestions for units that could be merged based on name similarity.
   */
  getSuggestions: async (sessionId: string, threshold: number = 0.8, autoMerge: boolean = false): Promise<UnitSuggestionsResponse> => {
    const response = await api.get(`/units/suggestions/${sessionId}`, {
      params: { threshold, auto_merge: autoMerge }
    });
    const data = response.data;
    return {
      suggestions: data.suggestions.map((s: any) => ({
        units: s.units,
        similarity: s.similarity,
        suggestedName: s.suggested_name,
        reason: s.reason,
      })),
      threshold: data.threshold,
      autoMerged: (data.auto_merged || []).map((a: any) => ({
        mergedUnits: a.merged_units,
        targetUnit: a.target_unit,
        rowsAffected: a.rows_affected,
      })),
    };
  },
};

/**
 * Download a blob from an API endpoint and save it as a file.
 * @param path - API path (relative to API_BASE)
 * @param fallbackFilename - Default filename if Content-Disposition header is missing
 */
// The chat/agent endpoints block until the full agent turn finishes, which can
// span several LLM round-trips and tool calls. Give them a much larger timeout
// than the 30s default so long turns are not cut off on the client. Cancel and
// getTools stay on the default since they return quickly.
const CHAT_REQUEST_TIMEOUT_MS = 300000; // 5 minutes

export const chatAPI = {
  getTools: async (
    sessionId?: string,
    sessionMode: 'schematiq' | 'load' = 'schematiq',
  ): Promise<ChatToolInfo[]> => {
    const params = new URLSearchParams({ session_mode: sessionMode });
    if (sessionId) {
      params.set('session_id', sessionId);
    }
    const response = await api.get(`/chat/tools?${params.toString()}`);
    return response.data.tools;
  },

  getMessages: async (sessionId: string): Promise<ChatMessagesResponse> => {
    // Read-only transcript for repainting the chat after a refresh/redeploy.
    const response = await api.get(`/chat/${sessionId}/messages`);
    return response.data;
  },

  sendMessage: async (
    sessionId: string,
    payload: {
      message: string;
      chat_id?: string;
      session_mode: 'schematiq' | 'load';
      pinned_tool?: string;
      model?: string;
    },
    signal?: AbortSignal,
  ): Promise<ChatMessageResponse> => {
    const response = await api.post(`/chat/${sessionId}/message`, payload, { timeout: CHAT_REQUEST_TIMEOUT_MS, signal });
    return response.data;
  },

  confirmAction: async (
    sessionId: string,
    chatId: string,
    signal?: AbortSignal,
  ): Promise<ChatMessageResponse> => {
    const response = await api.post(`/chat/${sessionId}/confirm`, { chat_id: chatId }, { timeout: CHAT_REQUEST_TIMEOUT_MS, signal });
    return response.data;
  },

  cancelAction: async (
    sessionId: string,
    chatId: string,
  ): Promise<ChatMessageResponse> => {
    const response = await api.post(`/chat/${sessionId}/cancel`, { chat_id: chatId });
    return response.data;
  },

  // Tells the backend to halt the in-flight turn and discard it. Fire-and-forget:
  // the client aborts its own request separately, so a failure here only means
  // the server keeps working until the turn ends on its own.
  stopChat: async (sessionId: string): Promise<{ stopped: boolean }> => {
    const response = await api.post(`/chat/${sessionId}/stop`);
    return response.data;
  },
};

export interface ReferenceDocumentInfo {
  id: string;
  filename: string;
  char_count: number;
  truncated: boolean;
}

/**
 * External reference documents attached to a session. These are supplementary
 * lookup material (e.g. a spreadsheet mapping entities to extra attributes),
 * distinct from the source documents that define observation-unit rows.
 */
export const referenceAPI = {
  list: async (sessionId: string): Promise<ReferenceDocumentInfo[]> => {
    const response = await api.get(`/reference/${sessionId}`);
    return response.data.reference_documents;
  },

  upload: async (sessionId: string, file: File): Promise<ReferenceDocumentInfo> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/reference/${sessionId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  remove: async (sessionId: string, referenceId: string): Promise<void> => {
    await api.delete(`/reference/${sessionId}/${referenceId}`);
  },
};

export async function downloadBlob(path: string, fallbackFilename: string): Promise<void> {
  const response = await api.get(path, { responseType: 'blob' });

  // Try to extract filename from Content-Disposition header
  const disposition = response.headers['content-disposition'];
  let filename = fallbackFilename;
  if (disposition) {
    const match = disposition.match(/filename=(.+?)(?:;|$)/);
    if (match) {
      filename = match[1].replace(/["']/g, '').trim();
    }
  }

  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

export default api;